import bookmarkIcon from "@tabler/icons/outline/bookmark.svg";
import heartIcon from "@tabler/icons/outline/heart.svg";
import messageCircle2Icon from "@tabler/icons/outline/message-circle-2.svg";
import quoteIcon from "@tabler/icons/outline/quote.svg";
import repeatIcon from "@tabler/icons/outline/repeat.svg";
import uploadIcon from "@tabler/icons/outline/upload.svg";
import React, { useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useGroupRelationship } from "@/api/group-relationships";
import DropdownMenu from "@/components/dropdown-menu";
import { HStack } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useFeatures, useStatusActions } from "@/hooks";
import { type Group, type Status } from "@/schemas";
import { cn } from "@/utils/cn";

import GroupPopover from "./popovers/group-popover";
import StatusActionGroup, {
  type StatusActionLayout,
} from "./statuses/status-action-group";
import StatusMenu from "./statuses/status-menu";
import StatusReactions from "./statuses/status-reactions";

const messages = defineMessages({
  bookmark: { id: "status.boookmark", defaultMessage: "Bookmark" },
  cancel_reblog_private: {
    id: "status.cancel_reblog_private",
    defaultMessage: "Un-ReTruth",
  },
  cannot_reblog: {
    id: "status.cannot_reblog",
    defaultMessage: "This Truth cannot be ReTruthed",
  },
  favourite: { id: "status.favourite", defaultMessage: "Like" },
  mention: { id: "status.mention", defaultMessage: "Mention @{name}" },
  privateGroupTitle: {
    id: "status.private_group_title",
    defaultMessage:
      "Only members of this private group will see this Truth in their Groups feed.",
  },
  quotePost: { id: "status.quote", defaultMessage: "Quote Truth" },
  reblog: { id: "status.reblog", defaultMessage: "ReTruth" },
  reblog_private: {
    id: "status.reblog_private",
    defaultMessage: "ReTruth to original audience",
  },
  redraft: { id: "status.redraft", defaultMessage: "Delete & re-draft" },
  replies_disabled_group: {
    id: "status.disabled_replies.group_membership",
    defaultMessage: "Only group members can reply",
  },
  reply: { id: "status.reply", defaultMessage: "Reply" },
  replyAll: { id: "status.replyAll", defaultMessage: "Reply to thread" },
  share: { id: "status.share", defaultMessage: "Share" },
  unbookmark: { id: "status.unboookmark", defaultMessage: "Remove Bookmark" },
});

interface StatusActionBarProps {
  expandable?: boolean;
  isRecommended?: boolean;
  layout: StatusActionLayout;
  status: Status;
  excludeMenu?: boolean;
}

const StatusActionBar: React.FC<StatusActionBarProps> = ({
  isRecommended = false,
  layout,
  status,
  expandable = true,
  excludeMenu = false,
}) => {
  const intl = useIntl();
  const features = useFeatures();
  const { currentAccount } = useAuth();
  const { groupRelationship } = useGroupRelationship(status.group?.id);

  const {
    handleBookmark,
    handleReblog,
    handleFavourite,
    handleReply,
    handleQuote,
    handleShare,
  } = useStatusActions({ status });

  const isGroupStatus = typeof status.group === "object" && !!status.group;

  const [isGroupPopoverOpen, setIsGroupPopoverOpen] = useState(false);

  if (!status) {
    return null;
  }

  const publicStatus = ["public", "unlisted", "group"].includes(
    status.visibility,
  );

  const replyCount = status.replies_count;
  const reblogCount = status.reblogs_count;
  const favouriteCount = status.favourites_count;

  const reblogIcon = repeatIcon;
  let replyTitle;
  let actionDisabled = false;

  if (
    !(status.group as Group)?.event_category_name &&
    (status.group as Group)?.membership_required &&
    !groupRelationship?.member
  ) {
    actionDisabled = true;
    replyTitle = intl.formatMessage(messages.replies_disabled_group);
  }

  const reblogMenu = [
    {
      text: intl.formatMessage(
        status.reblogged ? messages.cancel_reblog_private : messages.reblog,
      ),
      action: handleReblog,
      icon: repeatIcon,
    },
    {
      text: intl.formatMessage(messages.quotePost),
      action: handleQuote,
      icon: quoteIcon,
    },
  ];

  const groupReblogMenu = [];

  if (status.group?.locked) {
    groupReblogMenu.push(
      {
        text: intl.formatMessage(messages.privateGroupTitle),
        paragraph: true,
      },
      null,
    );
  }

  groupReblogMenu.push(
    {
      text: intl.formatMessage(
        status.reblogged ? messages.cancel_reblog_private : messages.reblog,
      ),
      action: handleReblog,
      icon: repeatIcon,
    },
    {
      text: intl.formatMessage(messages.quotePost),
      action: handleQuote,
      icon: quoteIcon,
    },
  );

  const reblogButton = (
    <StatusActionGroup
      layout={layout}
      primaryAction={{
        count: reblogCount,
        handler: handleReblog,
        iconSrc: reblogIcon,
        color: "success",
        disabled: !publicStatus,
        active: status.reblogged,
        "aria-label": !publicStatus
          ? intl.formatMessage(messages.cannot_reblog)
          : intl.formatMessage(messages.reblog),
      }}
    />
  );

  if (!status.in_reply_to_id) {
    replyTitle = intl.formatMessage(messages.reply);
  } else {
    replyTitle = intl.formatMessage(messages.replyAll);
  }

  const canShare =
    "share" in navigator &&
    (status.visibility === "public" || status.visibility === "group");

  return (
    <HStack
      justifyContent="between"
      space={3}
      grow={layout === "details"}
      onClick={(e) => e.stopPropagation()}
      alignItems={layout === "details" ? "stretch" : undefined}
      className={cn("cursor-default", {
        "*:w-full": layout === "details",
      })}
      data-testid="status-action-bar"
    >
      <GroupPopover
        group={status.group as Group}
        isEnabled={actionDisabled}
        showAction
        isOpen={isGroupPopoverOpen}
        onOpenChange={setIsGroupPopoverOpen}
      >
        <StatusActionGroup
          layout={layout}
          primaryAction={{
            count: replyCount,
            handler: () => {
              if (actionDisabled) return;

              handleReply();
            },
            iconSrc: messageCircle2Icon,
            color: "primary",
            "aria-label": replyTitle,
          }}
        />
      </GroupPopover>

      {currentAccount ? (
        <DropdownMenu
          items={isGroupStatus ? groupReblogMenu : reblogMenu}
          disabled={!publicStatus}
          onShiftClick={handleReblog}
          width={status.group?.locked ? "wide" : "normal"}
        >
          {reblogButton}
        </DropdownMenu>
      ) : (
        reblogButton
      )}

      {features.groupVotes && status.votable ? (
        <StatusReactions status={status} layout={layout} />
      ) : (
        <StatusActionGroup
          primaryAction={{
            "aria-label": intl.formatMessage(messages.favourite),
            iconSrc: heartIcon,
            color: "accent",
            isFilledOnActive: true,
            handler: handleFavourite,
            active: status.favourited,
            count: favouriteCount,
          }}
          layout={layout}
        />
      )}

      <StatusActionGroup
        primaryAction={{
          "aria-label": intl.formatMessage(
            status.bookmarked ? messages.unbookmark : messages.bookmark,
          ),
          iconSrc: bookmarkIcon,
          color: "primary",
          active: status.bookmarked,
          handler: handleBookmark,
        }}
        layout={layout}
      />

      {canShare && (
        <StatusActionGroup
          primaryAction={{
            "aria-label": intl.formatMessage(messages.share),
            iconSrc: uploadIcon,
            handler: handleShare,
            color: "default",
          }}
          layout={layout}
        />
      )}

      {layout !== "details" && !excludeMenu && (
        <StatusMenu
          isExpandable={expandable}
          isRecommended={isRecommended}
          layout={layout}
          status={status}
        />
      )}
    </HStack>
  );
};

export default StatusActionBar;
